/* Eloss — product form
 *
 * Spec: rule 9 (the product-form DOM contract), rule 10 (whole-form FormData
 * submits; line item properties are never stripped, filtered or
 * reconstructed), §F.2 item 9 (ATC states), §P.5, §P.6. Decision D-4.
 *
 * RULE 10 IS THE WHOLE POINT OF THIS FILE:
 *
 *     const body = new FormData(this.form);
 *
 * The entire form is submitted, exactly as the browser would submit it. No
 * manual {id, quantity} object is ever constructed. This matters because
 * Kickflip writes its configuration into the cart as line item properties on
 * this same form; a hand-built payload would silently drop every one of them
 * and the customer would receive an unconfigured watch. That failure would be
 * invisible in testing with an ordinary product.
 *
 * PROGRESSIVE ENHANCEMENT (§P.5, "mandatory for every purchase-critical
 * path"). Everything here is additive. With this file absent or broken the
 * form still performs a native POST to /cart/add and Shopify redirects to the
 * cart. The one thing connect() MUST do is enable the hidden `id` input — see
 * snippets/product-form.liquid for why it ships disabled.
 *
 * PHASE 4 (§H.1). On success the response's server-rendered sections are
 * swapped in by Eloss.cartSwap and eloss:cart:added is emitted. The drawer
 * opens by listening for that event, not by being told to from here, so there
 * is one event contract rather than two call paths. A 422 never reaches that
 * emit, which is how §H.2's "errors render inline near the ATC and do not open
 * the drawer" is enforced.
 */

(() => {
  'use strict';

  const Eloss = window.Eloss;
  if (!Eloss) return;

  /* §H.1, verbatim: "sections=cart-drawer,cart-bubble". */
  const CART_SECTIONS = 'cart-drawer,cart-bubble';

  Eloss.define(
    'eloss-product-form',
    class ElossProductForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;

        this.submitButton = this.querySelector('.product-form__submit');
        this.errorTarget = this.querySelector('[data-form-error]');
        this.variantInput = this.querySelector('[data-variant-id]');

        /* The contract input ships disabled so a no-JS page submits the
           <noscript> select instead of two `id` values. We are running, so the
           hidden input becomes the single source of truth. */
        if (this.variantInput) this.variantInput.disabled = false;

        this.onSubmit = (event) => this.handleSubmit(event);
        this.form.addEventListener('submit', this.onSubmit);
      }

      disconnectedCallback() {
        if (this.form && this.onSubmit) {
          this.form.removeEventListener('submit', this.onSubmit);
        }
        if (this.abortController) this.abortController.abort();
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;
        /* §F.2 item 9: "loading (aria-busy, label retained)". The label is
           never swapped for a spinner — a control that loses its name mid
           interaction is unusable with a screen reader. */
        this.submitButton.setAttribute('aria-busy', String(isLoading));
        this.submitButton.disabled = isLoading;
      }

      showError(message) {
        if (!this.errorTarget) return;
        this.errorTarget.textContent = message;
        this.errorTarget.hidden = false;
      }

      clearError() {
        if (!this.errorTarget) return;
        this.errorTarget.textContent = '';
        this.errorTarget.hidden = true;
      }

      async handleSubmit(event) {
        event.preventDefault();
        if (this.submitButton && this.submitButton.getAttribute('aria-busy') === 'true') {
          return;
        }

        this.clearError();
        this.setLoading(true);

        if (this.abortController) this.abortController.abort();
        this.abortController = new AbortController();

        /* RULE 10. The whole form, untouched. */
        const body = new FormData(this.form);

        /* §H.1 requires every cart mutation to request
           `sections=cart-drawer,cart-bubble` and swap the returned HTML.
           These two fields are APPENDED to the form's own FormData — nothing is
           removed, filtered, copied out or rebuilt, so rule 10 holds exactly as
           it did: every field the form carries, including app-injected
           properties[...], is still submitted verbatim (decision 14.2). */
        body.append('sections', CART_SECTIONS);
        body.append('sections_url', window.location.pathname);

        try {
          const response = await fetch(this.form.action, {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
            body,
            signal: this.abortController.signal,
          });

          const data = await response.json();

          if (!response.ok) {
            /* Shopify returns { status, message, description } on refusal —
               out of stock, quantity above what is available, and so on. Its
               description is already localized for the storefront locale, so
               it is shown as-is rather than replaced with a generic string. */
            const message =
              data.description || data.message || this.genericError();
            this.showError(message);
            Eloss.emit(Eloss.EVENTS.cartError, { source: 'product-form', data });
            return;
          }

          /* §H.1 — swap the server-rendered sections. This replaces the phase 3
             /cart.js count refresh, which was a documented placeholder for
             exactly this work. The header badge now comes from cart-bubble,
             the authoritative count, and is correct even when the cart started
             empty — the gap phase 3 recorded. */
          if (typeof Eloss.cartSwap === 'function') {
            Eloss.cartSwap(data.sections, {
              announce: { key: 'added', title: data.product_title || data.title || '' },
            });
          }

          /* Emitted only on success, so a 422 can never open the drawer
             (§H.2). The drawer listens for this event rather than being
             opened from here — one event contract, no parallel system. */
          Eloss.emit(Eloss.EVENTS.cartAdded, {
            item: data,
            variantId: data.variant_id || data.id,
            quantity: data.quantity,
          });
        } catch (error) {
          if (error.name === 'AbortError') return;
          this.showError(this.genericError());
          Eloss.emit(Eloss.EVENTS.cartError, {
            source: 'product-form',
            error: String(error),
          });
        } finally {
          this.setLoading(false);
        }
      }

      genericError() {
        return this.dataset.errorMessage || '';
      }

    }
  );
})();
